/**
 * Web Audio mixes all inputs connected to one destination. Each participant
 * gets a gain node, so a future UI can add individual volume/mute controls.
 * The clock maps a sender's capture timestamps to one local playout timeline.
 */
export class ConferenceMediaClock {
  constructor(targetLatencyMs = 220) {
    this.targetLatencyUs = targetLatencyMs * 1000;
    this.origins = new Map();
  }

  presentationUs(channel, captureTimeUs) {
    const nowUs = performance.now() * 1000;
    let origin = this.origins.get(channel);
    if (!origin) {
      origin = { captureTimeUs, presentationUs: nowUs + this.targetLatencyUs };
      this.origins.set(channel, origin);
    }
    return origin.presentationUs + captureTimeUs - origin.captureTimeUs;
  }

  audioTime(context, channel, captureTimeUs) {
    const nowUs = performance.now() * 1000;
    let dueUs = this.presentationUs(channel, captureTimeUs);
    const lateUs = nowUs - dueUs;
    const tooFarAheadUs = dueUs - nowUs;
    if (lateUs > 250_000 || tooFarAheadUs > 2_000_000) {
      const origin = { captureTimeUs, presentationUs: nowUs + this.targetLatencyUs };
      this.origins.set(channel, origin);
      dueUs = origin.presentationUs + captureTimeUs - origin.captureTimeUs;
    }
    return Math.max(context.currentTime + 0.01, context.currentTime + (dueUs - nowUs) / 1_000_000);
  }

  videoDelayMs(channel, captureTimeUs) {
    return Math.max(0, (this.presentationUs(channel, captureTimeUs) - performance.now() * 1000) / 1000);
  }

  remove(channel) { this.origins.delete(channel); }
  clear() { this.origins.clear(); }
}

/** Аварийный потолок fallback-очереди (~400 мс). */
const FALLBACK_MAX_SEC = 0.4;

/**
 * Conference mixer: PCM goes to AudioWorklet with a ring buffer,
 * without a separate BufferSource per Opus frame.
 */
export class ConferenceAudioMixer {
  constructor({ onState = () => {}, onAudioDiagnostic = () => {}, onWarning = console.warn }) {
    this.onState = onState;
    this.onAudioDiagnostic = onAudioDiagnostic;
    this.onWarning = onWarning;
    this.participantSettings = new Map();
    this.fallbackQueues = new Map();
    this.workletStreams = new Set();
  }

  /** Creates AudioContext and connects worklet or fallback player. */
  async enable() {
    if (!this.context) {
      this.context = new AudioContext({ sampleRate: 48_000 });
      this.master = this.context.createGain();
      this.master.gain.value = 0.9;
      this.master.connect(this.context.destination);
      if (this.context.audioWorklet) {
        await this.context.audioWorklet.addModule(new URL("./conference-pcm-processor.js", import.meta.url));
        this.worklet = new AudioWorkletNode(this.context, "conference-pcm-processor");
        this.worklet.connect(this.master);
        this.worklet.port.onmessage = (event) => {
          const data = event.data;
          if (data?.type === "underrun") {
            this.onAudioDiagnostic({ type: "audio_underrun", count: data.count });
          } else if (data?.type === "buffer_stats") {
            this.onAudioDiagnostic({
              type: "audio_buffer_stats",
              bufferedMs: data.bufferedMs,
              targetMs: data.targetMs,
              catchUpMs: data.catchUpMs,
              count: data.underruns
            });
          }
        };
        this.mode = "worklet";
      } else {
        this.mode = "fallback";
        this.fallbackNextAt = new Map();
      }
    }
    await this.context.resume();
    this.onAudioDiagnostic({ type: "audio_mixer_running", state: this.context.state, mode: this.mode });
    this.onState({ label: `audio mixer: ${this.context.sampleRate} Hz · ${this.mode}` });
    return this.context.state;
  }

  /**
   * Accepts decoded mono PCM and queues it for playback.
   * @param {string} channel
   * @param {AudioData} audioData
   */
  async play(channel, audioData) {
    if (!this.context) {
      this.onAudioDiagnostic({ type: "audio_dropped_context", channel });
      audioData.close();
      return;
    }
    if (this.context.state === "suspended") {
      try {
        await this.context.resume();
        this.onAudioDiagnostic({ type: "audio_context_resumed", state: this.context.state });
      } catch (error) {
        this.onAudioDiagnostic({ type: "audio_dropped_context", channel });
        this.onWarning("[conference] AudioContext resume failed", { channel, error });
        audioData.close();
        return;
      }
    }
    if (this.context.state !== "running") {
      this.onAudioDiagnostic({ type: "audio_dropped_context", channel });
      audioData.close();
      return;
    }

    const frames = audioData.numberOfFrames;
    const samples = new Float32Array(frames);
    audioData.copyTo(samples, { planeIndex: 0, format: "f32-planar" });
    audioData.close();

    const gain = this.effectiveGain(channel);
    if (this.mode === "worklet") {
      this.ensureStreamSettings(channel);
      this.worklet.port.postMessage({ type: "pcm", channel, samples }, [samples.buffer]);
      this.onAudioDiagnostic({ type: "audio_played", channel });
      return;
    }

    this.playFallback(channel, samples, gain);
    this.onAudioDiagnostic({ type: "audio_played", channel });
  }

  /** Sends gain/mute to worklet on a participant's first frame. */
  ensureStreamSettings(channel) {
    if (!this.participantSettings.has(channel)) {
      this.participantSettings.set(channel, { volume: 1, muted: false });
    }
    if (this.workletStreams.has(channel)) return;
    this.workletStreams.add(channel);
    this.sendStreamSettings(channel);
  }

  /** Syncs participant volume with worklet. */
  sendStreamSettings(channel) {
    if (!this.worklet) return;
    const settings = this.participantSettings.get(channel) || {};
    this.worklet.port.postMessage({ type: "mute", channel, value: Boolean(settings.muted) });
    this.worklet.port.postMessage({ type: "gain", channel, value: this.effectiveGain(channel) });
  }

  /**
   * Fallback without AudioWorklet: schedules short buffers back-to-back without gaps.
   * @param {string} channel
   * @param {Float32Array} samples
   * @param {number} gain
   */
  playFallback(channel, samples, gain) {
    let state = this.fallbackQueues.get(channel);
    if (!state) {
      state = { queue: [], queueFrames: 0, scheduledUntil: 0, gainNode: this.context.createGain() };
      state.gainNode.connect(this.master);
      this.fallbackQueues.set(channel, state);
    }
    state.gainNode.gain.value = gain;
    state.queue.push(samples);
    state.queueFrames += samples.length;
    const maxFrames = Math.floor(this.context.sampleRate * FALLBACK_MAX_SEC);
    while (state.queueFrames > maxFrames && state.queue.length > 1) {
      const dropped = state.queue.shift();
      state.queueFrames -= dropped.length;
    }
    this.scheduleFallback(channel, state);
  }

  /** Schedules PCM from the fallback queue ahead on the AudioContext timeline. */
  scheduleFallback(channel, state) {
    const lead = 0.08;
    const now = this.context.currentTime;
    if (!state.scheduledUntil || state.scheduledUntil < now) {
      state.scheduledUntil = now + lead;
    }
    while (state.queue.length > 0 && state.scheduledUntil < now + FALLBACK_MAX_SEC) {
      const chunk = state.queue.shift();
      state.queueFrames -= chunk.length;
      const buffer = this.context.createBuffer(1, chunk.length, this.context.sampleRate);
      buffer.copyToChannel(chunk, 0);
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.connect(state.gainNode);
      try {
        source.start(state.scheduledUntil);
        state.scheduledUntil += chunk.length / this.context.sampleRate;
      } catch (error) {
        this.onAudioDiagnostic({ type: "audio_dropped_start", channel });
        this.onWarning("[conference] fallback audio start failed", { channel, error });
        break;
      }
    }
  }

  setVolume(channel, volume) {
    const settings = { ...(this.participantSettings.get(channel) || {}), volume: Math.max(0, Math.min(2, volume)) };
    this.participantSettings.set(channel, settings);
    this.sendStreamSettings(channel);
    const state = this.fallbackQueues.get(channel);
    if (state) state.gainNode.gain.value = this.effectiveGain(channel);
  }

  setMuted(channel, muted) {
    const settings = { ...(this.participantSettings.get(channel) || {}), muted: Boolean(muted) };
    this.participantSettings.set(channel, settings);
    this.sendStreamSettings(channel);
    const state = this.fallbackQueues.get(channel);
    if (state) state.gainNode.gain.value = this.effectiveGain(channel);
  }

  effectiveGain(channel) {
    const settings = this.participantSettings.get(channel) || {};
    return settings.muted ? 0 : settings.volume ?? 1;
  }

  remove(channel) {
    this.participantSettings.delete(channel);
    this.workletStreams.delete(channel);
    this.worklet?.port.postMessage({ type: "remove", channel });
    const state = this.fallbackQueues.get(channel);
    if (state) {
      state.gainNode.disconnect();
      this.fallbackQueues.delete(channel);
    }
  }

  close() {
    this.worklet?.port.postMessage({ type: "flush" });
    this.worklet?.disconnect();
    this.worklet = undefined;
    for (const state of this.fallbackQueues.values()) state.gainNode.disconnect();
    this.fallbackQueues.clear();
    this.participantSettings.clear();
    this.workletStreams.clear();
    this.master?.disconnect();
    this.context?.close().catch(this.onWarning);
    this.context = undefined;
    this.mode = undefined;
  }
}
